import type { RequirementMinimum, ServerBand } from "../api/domain.ts";
import type { Dictionary, Locale } from "../i18n/dictionary.ts";

/** Display-only presentation bands from CONCEPT.md; they never feed the math. */
export type Band = "missing" | "partial" | "solid" | "excellent";

export const bandOf = (score: number): Band => {
  if (score < 1.5) return "missing";
  if (score < 3) return "partial";
  if (score < 4.25) return "solid";
  return "excellent";
};

export const bandLabel = (dictionary: Dictionary, band: Band): string => dictionary.band[band];

/**
 * Two decimals, localized separator; scores live on the 0 to 5 scale.
 *
 * A weighted mean of many answers separates two products by hundredths, and one
 * decimal rounded those apart products onto the same number.
 */
export const formatScore = (score: number, locale: Locale): string =>
  new Intl.NumberFormat(locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(score);

export type Coverage = {
  answered: number;
  total: number;
};

/** 0 when nothing is in scope, so callers never divide by zero. */
export const coverageRatio = (coverage: Coverage): number =>
  coverage.total === 0 ? 0 : coverage.answered / coverage.total;

export const formatPercent = (ratio: number, locale: Locale): string =>
  new Intl.NumberFormat(locale, { style: "percent", maximumFractionDigits: 0 }).format(ratio);

export const formatCoverage = (coverage: Coverage, locale: Locale): string =>
  `${coverage.answered}/${coverage.total} (${formatPercent(coverageRatio(coverage), locale)})`;

/** The server's spelling of a band, mapped onto the display band. */
export const bandFromServer = (band: ServerBand): Band =>
  band === "missing_or_weak" ? "missing" : band;

/**
 * The band to show for a score the server already banded. The server's own band
 * wins; the local thresholds only fill in when a payload carries none.
 */
export const bandFor = (score: number | null, band: ServerBand | null): Band | undefined => {
  if (band !== null) return bandFromServer(band);
  if (score === null) return undefined;
  return bandOf(score);
};

/** The threshold a requirement demands, written for a reader. */
export const formatMinimum = (
  dictionary: Dictionary,
  minimum: RequirementMinimum,
  locale: Locale,
): string =>
  minimum.kind === "score"
    ? `${dictionary.common.score} >= ${formatScore(minimum.value, locale)}`
    : `${dictionary.common.band} >= ${bandLabel(dictionary, bandFromServer(minimum.value))}`;

/** Milliseconds since the epoch as a readable local date and time. */
export const formatMoment = (millis: number, locale: Locale): string =>
  new Intl.DateTimeFormat(locale, { dateStyle: "medium", timeStyle: "short" }).format(
    new Date(millis),
  );

/**
 * How long ago a moment was, in the locale's own words: "now", "15 min. ago",
 * "vor 2 Std.". Hours are the coarsest unit, since a magic-link session does
 * not outlive a day.
 */
export const formatAgo = (millis: number, now: number, locale: Locale): string => {
  // English narrow reads "15m ago"; German narrow gives "vor 15 m", so it takes short.
  const relative = new Intl.RelativeTimeFormat(locale, {
    numeric: "auto",
    style: locale === "de" ? "short" : "narrow",
  });
  const seconds = Math.max(0, Math.round((now - millis) / 1000));
  if (seconds < 60) return relative.format(0, "second");
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return relative.format(-minutes, "minute");
  return relative.format(-Math.round(minutes / 60), "hour");
};

/** A moment as the value an `<input type="datetime-local">` expects. */
export const momentInputValue = (millis: number): string => {
  const at = new Date(millis - new Date(millis).getTimezoneOffset() * 60_000);
  return at.toISOString().slice(0, 16);
};

/**
 * A number as a person would type it: the locale's own separator, no grouping.
 *
 * Grouping is off because the string goes back into an input, and a separator
 * that means "thousands" here and "decimal" there cannot survive a round trip.
 */
export const formatDecimal = (value: number, locale: Locale): string =>
  new Intl.NumberFormat(locale, { useGrouping: false, maximumFractionDigits: 10 }).format(value);

/**
 * A number a person typed, taking either decimal separator.
 *
 * Undefined for anything that is not one number, which is what lets a field
 * hold an unfinished entry on screen without reporting a value for it.
 */
export const parseDecimal = (text: string): number | undefined => {
  const cleaned = text.trim().replace(",", ".");
  if (!/^[+-]?\d*\.?\d*$/.test(cleaned)) return undefined;
  const value = Number(cleaned);
  return cleaned === "" || !Number.isFinite(value) ? undefined : value;
};

/** Two decimals; weights live between 0 and 1 and read badly when rounded harder. */
export const formatWeight = (weight: number, locale: Locale): string =>
  new Intl.NumberFormat(locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(
    weight,
  );
